import React, { useState, useRef, useEffect } from "react";
import Button from "../Button";

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  variant?: "default" | "danger";
  disabled?: boolean;
}

export interface ActionDropdownProps {
  items: ActionMenuItem[];
  className?: string;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({ items, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleItemClick = (item: ActionMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const handleButtonClick = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next && dropdownRef.current) {
        const button = dropdownRef.current.querySelector("button");
        if (button) {
          const rect = button.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 8,
            left: rect.right - 192, // 192px is the dropdown width
          });
        }
      }
      return next;
    });
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        className="p-2 hover:bg-zinc-700 bg-zinc-800 border border-zinc-700 text-white"
        onClick={handleButtonClick}
        aria-label="Actions"
      >
        ⋮
      </Button>

      {isOpen && dropdownPosition && (
        <div
          className="bg-zinc-900 border border-zinc-700 shadow-2xl z-[9999]"
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: "192px",
          }}
        >
          {items.map((item, index) => (
            <button
              key={index}
              type="button"
              disabled={item.disabled}
              className={`
                block w-full text-left px-4 py-2
                transition-colors duration-150
                ${item.variant === "danger" ? "text-red-400 hover:text-red-300 hover:bg-red-950/30" : "text-zinc-200 hover:text-white hover:bg-zinc-800"}
                ${item.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              `}
              onClick={() => handleItemClick(item)}
            >
              <div className="flex items-center gap-2">
                {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                <span>{item.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionDropdown;
